import AppConfigData from 'aws-sdk/clients/appconfigdata';
import CONSTANTS from '../utils/constants';

interface FeatureFlags {
  metricsDashboardEnabled: boolean;
  accountingCalendarEnabled: boolean;
  allowedUsers: string[];
  editLockingEnabled: boolean;
  testedPartyEnabled: boolean;
  standardizePLIEnabled: boolean;
  promotionEnabled: boolean;
}

export const EDIT_LOCKING_FLAG = 'editLockingEnabled' as const;

const DEFAULT_FLAGS: FeatureFlags = {
  metricsDashboardEnabled: false,
  accountingCalendarEnabled: false,
  allowedUsers: [],
  editLockingEnabled: false,
  testedPartyEnabled: false,
  standardizePLIEnabled: false,
  promotionEnabled: false,
};

let cachedFlags: FeatureFlags | null = null;
let cachedToken: string | null = null;

export async function getFeatureFlags(): Promise<FeatureFlags> {
  if (cachedFlags) {
    return cachedFlags;
  }

  const stage = CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE;
  if (!stage || stage.startsWith('{{') || stage === 'Dev') {
    return {
      metricsDashboardEnabled: true,
      accountingCalendarEnabled: true,
      allowedUsers: [],
      editLockingEnabled: false,
      testedPartyEnabled: true,
      standardizePLIEnabled: true,
      promotionEnabled: true,
    };
  }

  try {
    const client = new AppConfigData({
      region: CONSTANTS.ENVIRONMENT_VARIABLES.AWS_REGION,
    });

    const session = await client.startConfigurationSession({
      ApplicationIdentifier: 'ICTPFeatureFlagsApplication',
      EnvironmentIdentifier: 'ICTPFeatureFlagsEnvironment',
      ConfigurationProfileIdentifier: 'ICTPFeatureFlagsProfile',
    }).promise();

    cachedToken = session.InitialConfigurationToken || null;

    if (!cachedToken) {
      return DEFAULT_FLAGS;
    }

    const config = await client.getLatestConfiguration({
      ConfigurationToken: cachedToken,
    }).promise();

    if (config.Configuration) {
      const parsed = JSON.parse(config.Configuration.toString());
      cachedFlags = {
        metricsDashboardEnabled: parsed?.metricsDashboardEnabled?.enabled ?? false,
        accountingCalendarEnabled: parsed?.accountingCalendarEnabled?.enabled ?? false,
        allowedUsers: parsed?.metricsDashboardEnabled?.allowedUsers ?? [],
        editLockingEnabled: parsed?.editLockingEnabled?.enabled ?? false,
        testedPartyEnabled: parsed?.testedPartyEnabled?.enabled ?? false,
        standardizePLIEnabled: parsed?.standardizePLIEnabled?.enabled ?? false,
        promotionEnabled: parsed?.promotionEnabled?.enabled ?? false,
      };
      return cachedFlags;
    }
  } catch (error) {
    console.error('Failed to fetch feature flags from AppConfig:', error);
  }

  return DEFAULT_FLAGS;
}

export function isFeatureEnabledForUser(flags: FeatureFlags, featureName: 'metricsDashboardEnabled' | 'editLockingEnabled' | 'testedPartyEnabled', userAlias: string): boolean {
  if (!flags[featureName]) {
    return false;
  }
  if (featureName === 'metricsDashboardEnabled') {
    if (flags.allowedUsers.length === 0) {
      return true;
    }
    return flags.allowedUsers.includes(userAlias);
  }
  return true;
}
